<template>
  <div id="app" :class="{ 'is-workspace': hasSidebarLayout, 'has-compact-nav': hasCompactNav }">
    <SEOHeadTags
      :current-view="currentView"
      :current-view-name="currentViewName"
      :current-view-path="currentViewPath"
    />

    <NavBar :compact="hasCompactNav" :player="currentView === 'AudioPlayer'" />

    <!-- 移动端菜单背景遮罩 -->
    <div
      v-if="isMobileMenuOpen && showSidebar && currentView !== 'AudioPlayer'"
      class="mobile-menu-overlay"
      @click="closeMobileMenu"
    ></div>

    <div
      class="app-content"
      :class="{
        'has-sidebar': hasSidebarLayout,
        'is-home': currentView === 'HomePage',
        'is-player': currentView === 'AudioPlayer',
        'is-standalone': isStandalonePage
      }"
    >
      <div
        class="app-shell"
        :class="{
          'has-sidebar': hasSidebarLayout,
          'is-home': currentView === 'HomePage',
          'is-player': currentView === 'AudioPlayer',
          'is-standalone': isStandalonePage
        }"
      >
        <Sidebar
          v-if="hasSidebarLayout"
        />
        <div v-if="currentView !== 'HomePage'" style="display:none;" aria-hidden="true">
          <span id="breadcrumb-data"
            :data-current-view="currentViewName"
            :data-current-path="currentViewPath">
          </span>
        </div>
        <router-view v-slot="{ Component }">
          <template v-if="isStandalonePage">
            <component :is="Component" />
          </template>
          <template v-else>
            <keep-alive include="HomePage">
              <MainContent />
            </keep-alive>
          </template>
        </router-view>
      </div>
    </div>

    <AuthPage v-if="isAuthRoute" />

    <PricingPage v-if="$route.name === 'Pricing'" :user-details="userDetails" />

    <!-- 重命名对话框 -->
    <DialogModal
      v-if="showRenameDialog"
      :show="showRenameDialog"
      title="Rename File"
      @close="closeRenameDialog"
    >
      <input
        type="text"
        v-model="newFileName"
        :placeholder="newFileName"
        @keyup.enter="confirmRename"
        ref="renameInput"
        class="rename-input"
      >
      <template #buttons>
        <button class="dialog-action secondary" @click="closeRenameDialog">Cancel</button>
        <button class="dialog-action primary" @click="confirmRename">Rename</button>
      </template>
    </DialogModal>

    <!-- 删除确认对话框 -->
    <DialogModal
      v-if="showDeleteDialog"
      :show="showDeleteDialog"
      title="Delete File"
      @close="closeDeleteDialog"
    >
      <p class="delete-message">Are you sure you want to delete "{{ currentFileName }}"?</p>
      <p class="delete-warning">This action cannot be undone.</p>
      <template #buttons>
        <button class="dialog-action secondary" @click="closeDeleteDialog">Cancel</button>
        <button class="dialog-action danger" @click="confirmDelete">Delete</button>
      </template>
    </DialogModal>

    <FeedbackDialog />
    <CheckInCelebration />
  </div>
</template>

<script>
import { ref, computed, provide, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router'
import { authRouteNames, localizedRouteName } from '@/router'
import { useI18n } from 'vue-i18n'
import NavBar from './components/layout/NavBar.vue'
import MainContent from './components/layout/MainContent.vue'
import Sidebar from './components/layout/Sidebar.vue'
import AuthPage from './components/auth/AuthPage.vue'
import PricingPage from './components/pricing/PricingPage.vue'
import DialogModal from './components/modals/DialogModal.vue'
import FeedbackDialog from './components/common/FeedbackDialog.vue'
import CheckInCelebration from './components/common/CheckInCelebration.vue'
import SEOHeadTags from './components/common/SEOHeadTags.vue'
import { useAuth } from '@/composables/auth'
import { useViews } from '@/composables/views'
import { useMobileMenu } from '@/composables/useMobileMenu'
import { useFileDialogs } from '@/composables/useFileDialogs'

export default {
  name: 'App',
  components: {
    NavBar,
    MainContent,
    AuthPage,
    Sidebar,
    PricingPage,
    DialogModal,
    FeedbackDialog,
    CheckInCelebration,
    SEOHeadTags
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { user, userDetails } = useAuth()
    const { currentView, contentProps } = useViews()
    const { isMobileMenuOpen, showSidebar, closeMobileMenu } = useMobileMenu()

    const standaloneRoutes = ['PaymentSuccess', 'PaymentCancel', 'TermsOfService', 'PrivacyPolicy', 'RefundPolicy', 'MaterialAdmin', 'PartnerAdmin', 'Partner', 'LearnIndex', 'ZhLearnIndex', 'LearnArticle', 'ZhLearnArticle']
    const isStandalonePage = computed(() => standaloneRoutes.includes(route.name))
    const isAuthRoute = computed(() => authRouteNames.includes(route.name))
    const hasSidebarLayout = computed(() => (
      showSidebar.value && currentView.value !== 'AudioPlayer' && !isStandalonePage.value
    ))

    // 音频和文稿的播放页自带侧边栏（视频播放页没有），顶栏要和工作台一样矮，否则会盖住侧边栏顶部
    const hasCompactNav = computed(() => hasSidebarLayout.value || (
      currentView.value === 'AudioPlayer' && !contentProps.value.videoSrc && !contentProps.value.youtubeVideoId
    ))

    const {
      showRenameDialog,
      showDeleteDialog,
      newFileName,
      currentFileName,
      openRenameDialog,
      openDeleteDialog,
      closeRenameDialog,
      confirmRename,
      closeDeleteDialog,
      confirmDelete
    } = useFileDialogs()

    const { locale } = useI18n()

    const renameInput = ref(null)

    watch(
      locale,
      (value) => {
        document.documentElement.lang = value === 'zh' ? 'zh-CN' : 'en'
      },
      { immediate: true }
    )

    watch(
      () => route.fullPath,
      () => {
        if (route.name === 'CheckIn') {
          currentView.value = 'CheckIn'
          contentProps.value = {}
          return
        }

        if (route.meta.creationTool) {
          currentView.value = 'Creation'
          contentProps.value = { tool: route.meta.creationTool }
          return
        }

        if (route.meta.page === 'Home' && ['Creation', 'CheckIn'].includes(currentView.value)) {
          currentView.value = 'HomePage'
          contentProps.value = {}
          return
        }

        if (route.meta.page === 'Profile') {
          currentView.value = 'Profile'
          contentProps.value = {}
          return
        }

        if (route.meta.page === 'LevelTest') {
          currentView.value = 'LevelTest'
          contentProps.value = {}
          return
        }

        if (route.meta.page === 'Material') {
          currentView.value = 'Material'
          contentProps.value = {}
          return
        }

        if (['MaterialCategory', 'MaterialCollection', 'MaterialTest'].includes(route.meta.page)) {
          currentView.value = 'Material'
          contentProps.value = {
            ...contentProps.value,
            categoryId: route.params.id,
            collectionId: route.params.collectionId
          }
        }
      },
      { immediate: true }
    )

    // SEO: Computed properties for breadcrumbs
    const currentViewName = computed(() => {
      const viewNames = {
        'HomePage': 'Home',
        'AudioPlayer': 'Audio Player',
        'RecentFiles': 'Recent Files',
        'LearningRecords': 'Learning Records',
        'VocabularyBook': 'Vocabulary',
        'MaterialLibrary': 'Materials Library',
        'MaterialCategory': 'Materials Category'
      };
      return viewNames[currentView.value] || currentView.value;
    });

    const currentViewPath = computed(() => {
      const viewPaths = {
        'HomePage': '',
        'AudioPlayer': 'player',
        'RecentFiles': 'files',
        'LearningRecords': 'learning-records',
        'VocabularyBook': 'vocabulary',
        'MaterialLibrary': 'materials',
        'MaterialCategory': 'materials/category'
      };
      return viewPaths[currentView.value] || '';
    });

    // 提供给子组件的方法（重命名/删除对话框）
    provide('dialog', {
      openRenameDialog: (file, name) => openRenameDialog(file, name, renameInput),
      openDeleteDialog
    })

    // 初始化 locale
    const savedLocale = localStorage.getItem('userLocale');
    if (savedLocale) {
      locale.value = savedLocale;
    }

    // 首页和素材库的地址跟语言同步：进入 /zh/… 切到中文；停在这些页面时，语言变了就换到对应语言的地址
    watch([() => route.name, locale], ([name, currentLocale], [previousName]) => {
      const { page, locale: pageLocale } = route.meta
      if (!page) return
      if (pageLocale === 'zh' && name !== previousName) {
        locale.value = 'zh'
        return
      }
      if (pageLocale !== currentLocale) {
        router.replace({
          name: localizedRouteName(page, currentLocale),
          params: route.params,
          query: route.query,
          hash: route.hash
        })
      }
    });

    // 监听路由查询参数
    if (router) {
      router.afterEach((to) => {
        if (to.query.showUpgrade === 'true') {
          // 处理升级提示逻辑
        }
      })
    }

    return {
      user,
      userDetails,
      currentView,
      showSidebar,
      showRenameDialog,
      showDeleteDialog,
      newFileName,
      currentFileName,
      renameInput,
      closeRenameDialog,
      confirmRename,
      closeDeleteDialog,
      confirmDelete,
      currentViewName,
      currentViewPath,
      isMobileMenuOpen,
      closeMobileMenu,
      isStandalonePage,
      isAuthRoute,
      hasSidebarLayout,
      hasCompactNav
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
}

@supports (overflow: clip) {
  html,
  body {
    overflow-x: clip;
  }
}

#app {
  font-family: var(--font-family-base);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  position: relative;
  background: var(--color-page);
}

@supports (overflow: clip) {
  #app {
    overflow-x: clip;
  }
}

#app::before {
  content: '';
  position: fixed;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(17, 24, 39, 0.028) 1px, transparent 1px),
    linear-gradient(180deg, rgba(17, 24, 39, 0.024) 1px, transparent 1px),
    linear-gradient(118deg, rgba(23, 107, 77, 0.08) 0%, transparent 24%),
    linear-gradient(242deg, rgba(53, 199, 201, 0.07) 0%, transparent 28%),
    linear-gradient(180deg, #f4f6ef 0%, #fbfcf7 46%, #eef6f2 100%);
  background-size: 56px 56px, 56px 56px, auto, auto, auto;
  z-index: 0;
  pointer-events: none;
}

/* 矮顶栏下，播放页按矮顶栏的高度留出顶部空间 */
#app.has-compact-nav {
  --navbar-height: var(--app-navbar-height);
}

/* 工作台：顶栏、侧边栏、画布同一纸色，只用细线分隔，不显示营销页的网格纹 */
#app.is-workspace::before {
  display: none;
}

.app-content {
  min-height: calc(100vh - var(--navbar-height));
  position: relative;
  z-index: auto;
  display: flex;
  justify-content: center;
  padding: calc(var(--navbar-height) + var(--space-5)) var(--space-6) var(--space-8);
  width: 100%;
}

.app-content.is-home,
.app-content.is-player,
.app-content.is-standalone {
  display: block;
  padding: 0;
}

.app-shell {
  width: 100%;
  max-width: var(--content-max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: var(--space-6);
  min-width: 0;
}

.app-content.has-sidebar {
  display: block;
  min-height: 100vh;
  padding: calc(var(--app-navbar-height) + var(--space-8)) var(--space-10) var(--space-8)
    calc(var(--sidebar-width) + var(--space-10));
}

.app-shell.has-sidebar {
  display: block;
  max-width: var(--workspace-page-max-width);
}

.app-shell.is-home,
.app-shell.is-player,
.app-shell.is-standalone {
  max-width: none;
  display: block;
}

/* 对话框样式 */
.rename-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  transition: border-color 0.2s ease;
  outline: none;
}

.rename-input:focus {
  border-color: var(--color-brand-500);
  box-shadow: var(--focus-ring-brand);
}

.delete-message {
  font-size: 1rem;
  color: var(--color-text);
  margin: 0 0 0.5rem;
}

.delete-warning {
  font-size: 0.875rem;
  color: var(--color-danger);
  margin: 0;
}

.dialog-action {
  padding: 0.5rem 1rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
}

.dialog-action.primary {
  background: var(--color-brand-500);
  color: var(--color-text-inverse);
}

.dialog-action.secondary {
  background: var(--color-surface-muted);
  color: var(--color-text-muted);
}

.dialog-action.danger {
  background: var(--color-danger);
  color: var(--color-text-inverse);
}

.dialog-action.primary:hover {
  background: var(--color-brand-600);
}

.dialog-action.secondary:hover {
  background: var(--color-border);
}

.dialog-action.danger:hover {
  background: var(--color-danger-400);
}

.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-overlay);
  z-index: 999;
  backdrop-filter: blur(2px);
  transition: opacity 0.3s ease;
}

@media (max-width: 768px) {
  .app-content {
    padding: calc(4rem + var(--space-4)) var(--space-4) var(--space-4);
  }

  .app-content.has-sidebar {
    padding: calc(3.5rem + var(--space-4)) var(--space-4) var(--space-4);
  }
}
</style>
